import Toast from './Toast';

/**
 * Validates the file before upload.
 * @param {File} file - The file to validate.
 * @returns {boolean} The validation result.
 */
const validationType = (file: File): boolean => {
    /**
     * @type {string[]} validExtensions - Array of valid file extensions.
     * @type {string[]} validMimeTypes - Array of valid MIME types.
     */
    const validExtensions = ['.zip', '.ZIP'];
    const validMimeTypes = ['application/zip', 'application/x-zip-compressed'];

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    const fileType = file.type;

    if (
        validExtensions.includes(fileExtension) &&
        validMimeTypes.includes(fileType)
    ) {
        return true;
    }

    return false;
};

/**
 * Validates the documents before upload.
 * @param {File} file - The file to validate.
 * @returns {boolean} The validation result.
 */
const validationDocumentsRework = (file: File): boolean => {
    /**
     * @type {string[]} validExtensions - Array of valid file extensions.
     * @type {string[]} validMimeTypes - Array of valid MIME types.
     */
    const validExtensions = ['.pdf', '.PDF', '.doc', '.docx'];
    const validMimeTypes = [
        'application/pdf',
        'application/x-pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    const fileType = file.type;

    if (
        validExtensions.includes(fileExtension) &&
        validMimeTypes.includes(fileType)
    ) {
        return true;
    }

    return false;
};
/**
 * Validates the documents before upload.
 * @param {File} file - The file to validate.
 * @returns {boolean} The validation result.
 */
const validationDocuments = (file: File): boolean => {
    /**
     * @type {string[]} validExtensions - Array of valid file extensions.
     * @type {string[]} validMimeTypes - Array of valid MIME types.
     */
    const validExtensions = ['.pdf', '.PDF'];
    const validMimeTypes = ['application/pdf', 'application/x-pdf'];

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
    const fileType = file.type;

    if (
        validExtensions.includes(fileExtension) &&
        validMimeTypes.includes(fileType)
    ) {
        return true;
    }

    return false;
};
export const blobToData = (file: File): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (event) => {
            if (event && event.target) {
                resolve(event.target.result);
            } else {
                reject(new Error('File Read is error'));
            }
        };
        reader.readAsArrayBuffer(file);
    });

/**
 * Processes the file upload.
 * @param event - The file input change event or an array of File objects.
 * @param callback - The callback function to send the form data to.
 * @param maxSize - The maximum allowed file size in bytes.
 * @param maxFiles - The maximum allowed number of files.
 * @param type - The file came from, input or drag & drop.
 */
export const fileValidation = async (
    event: any,
    callback: (fileDataArray: { blobdata: Uint8Array; name: string }[]) => void,
    maxSize: number,
    maxFiles: number,
    type: string
): Promise<boolean> =>
    new Promise(async (resolve, reject) => {
        const files: FileList =
            type === 'input' || type === 'documents'
                ? event.target.files
                : event;
        const totalFiles: number = files.length;
        let validFileCount = 0;
        let totalSize = 0;
        const fileDataArray: { blobdata: Uint8Array; name: string }[] = [];

        if (totalFiles > maxFiles && maxFiles > 0) {
            const stringError = `Maximum file limit exceeded. Please select up to ${maxFiles} files.`;
            Toast.showErrorMessage(stringError);
            reject(new Error(stringError));
        }
        let errorValidation = false;
        for (let i = 0; i < totalFiles; i += 1) {
            const file: File = files[i];
            const validationStatus: boolean =
                // eslint-disable-next-line no-nested-ternary
                type === 'documents' || type === 'drop-rework'
                    ? (type === 'documents' &&
                          event.target.accept === '.pdf,.doc,.docx') ||
                      type === 'drop-rework'
                        ? validationDocumentsRework(file)
                        : validationDocuments(file)
                    : validationType(file);

            if (validationStatus) {
                const fileSize: number = file.size;
                totalSize += fileSize;

                if (totalSize > maxSize) {
                    const stringError = `Total file size exceeds the maximum allowed limit of ${
                        maxSize / (1024 * 1024 * 1024)
                    } GB. Please select files within the size limit.`;
                    Toast.showErrorMessage(stringError);
                    reject(new Error(stringError));
                    errorValidation = true;
                    break;
                }

                validFileCount += 1;
                // eslint-disable-next-line no-await-in-loop
                const result = await blobToData(file)
                    .then((res) => res)
                    .catch(() => false);
                if (result === null) {
                    Toast.showErrorMessage('File Read can not read data');
                    reject(new Error('File Read can not read data'));
                    errorValidation = true;
                    break;
                } else if (result === false) {
                    Toast.showErrorMessage('File Read is error');
                    reject(new Error('File Read is error'));
                    errorValidation = true;
                    break;
                }
                const fileData: Uint8Array = new Uint8Array(
                    result as ArrayBuffer
                );
                fileDataArray.push({ blobdata: fileData, name: file.name });
            } else {
                const stringError = `File (${
                    file.name
                }) format is not supported. Please select a ${
                    // eslint-disable-next-line no-nested-ternary
                    type === 'documents' || type === 'drop-rework'
                        ? (type === 'documents' &&
                              event.target.accept === '.pdf,.doc,.docx') ||
                          type === 'drop-rework'
                            ? 'pdf, doc, docx'
                            : 'pdf'
                        : 'zip'
                } file.`;
                Toast.showErrorMessage(stringError);
                reject(new Error(stringError));
                errorValidation = true;
                break;
            }
        }

        if (validFileCount === 0 && !errorValidation) {
            Toast.showErrorMessage('No valid files selected.');
            reject(new Error('No valid files selected.'));
        }

        callback(fileDataArray);

        resolve(true);
    });
